<template>
  <div class="tree-filter">
    <!-- PC端：使用 TreeSelect -->
    <a-tree-select
      v-if="!isMobile"
      :value="selectedKeys"
      :tree-data="treeData"
      :multiple="true"
      :tree-checkable="true"
      :tree-check-strictly="true"
      :show-checked-strategy="'SHOW_ALL'"
      :tree-default-expand-all="false"
      :max-tag-count="undefined"
      :show-search="false"
      placeholder="请选择"
      style="width: 100%"
      :dropdown-style="dropdownStyle"
      @change="handleChange"
    />
    
    <!-- 移动端：使用自定义树组件 -->
    <MobileTreeFilter
      v-else
      :nodes="treeData"
      :selected-keys="selectedKeys"
      @update:selectedKeys="handleTreeCheck"
    />
  </div>
</template>

<script>
import MobileTreeFilter from './MobileTreeFilter.vue'
import { isMobileDevice } from '@/utils/deviceDetection'

export default {
  name: 'TreeFilter',
  components: {
    MobileTreeFilter
  },
  props: {
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    modelValue: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      // TreeSelect 内部使用 id 数组（因为 id 是唯一标识）
      selectedKeys: [],
      isMobile: false
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
  },
  computed: {
    // 建立 id 到 name 的映射关系（用于传值给接口）
    idToNameMap() {
      const map = new Map()
      const buildMap = (nodes) => {
        nodes.forEach(node => {
          map.set(String(node.id), node.name)
          if (node.children && node.children.length > 0) {
            buildMap(node.children)
          }
        })
      }
      buildMap(this.options)
      return map
    },
    // 建立 name 到 id 的映射关系（用于从 name 数组转换为 id 数组）
    nameToIdMap() {
      const map = new Map()
      const buildMap = (nodes) => {
        nodes.forEach(node => {
          map.set(node.name, String(node.id))
          if (node.children && node.children.length > 0) {
            buildMap(node.children)
          }
        })
      }
      buildMap(this.options)
      return map
    },
    // 将 options 转换为 Ant Design TreeSelect 需要的格式
    treeData() {
      if (!this.options || !Array.isArray(this.options)) {
        return []
      }
      
      const convertNode = (node) => {
        if (!node) {
          return null
        }
        
        const converted = {
          title: node.name || '',
          value: String(node.id || ''),
          key: String(node.id || ''),
        }
        
        // 处理子节点：确保 children 存在且是数组
        if (node.children && Array.isArray(node.children) && node.children.length > 0) {
          const children = node.children
            .map(child => convertNode(child))
            .filter(child => child !== null)
          
          if (children.length > 0) {
            converted.children = children
          }
        }
        
        return converted
      }
      
      return this.options
        .map(option => convertNode(option))
        .filter(option => option !== null)
    },
    dropdownStyle() {
      // 移动端适配：下拉框样式
      if (this.isMobile) {
        return {
          maxHeight: '50vh',
          overflow: 'auto',
          minWidth: '90vw',
          maxWidth: '90vw'
        }
      }
      return {
        maxHeight: '600px',
        overflow: 'auto'
      }
    }
  },
  watch: {
    // 监听 modelValue 的变化，同步更新 selectedKeys
    modelValue: {
      immediate: true,
      handler(newValue) {
        if (!newValue || !Array.isArray(newValue)) {
          this.selectedKeys = []
          return
        }
        
        // 将 name 数组转换为 id 数组
        const ids = newValue
          .map(name => {
            const id = this.nameToIdMap.get(name)
            return id !== undefined ? id : null
          })
          .filter(id => id !== null)
          .map(id => String(id))
        
        this.selectedKeys = ids
      }
    }
  },
  methods: {
    handleChange(value) {
      // TreeSelect 返回的是 id 数组，更新内部状态
      this.selectedKeys = Array.isArray(value) ? value : []
      
      // 将 id 数组转换为 name 数组传给接口
      const names = Array.isArray(value)
        ? value.map(id => {
            // 如果 value 是对象，提取 value 属性
            if (id && typeof id === 'object' && 'value' in id) {
              id = id.value
            }
            // 将 id 转换为对应的 name
            const name = this.idToNameMap.get(String(id))
            return name !== undefined ? name : String(id)
          })
        : []
      this.$emit('update:modelValue', names)
    },
    handleTreeCheck(checkedKeys) {
      // 移动端 Tree 的 check 事件
      const keys = Array.isArray(checkedKeys) ? checkedKeys.map(k => String(k)) : []
      this.selectedKeys = keys
      
      // 将 id 数组转换为 name 数组传给接口
      const names = keys.map(id => {
        const name = this.idToNameMap.get(String(id))
        return name !== undefined ? name : String(id)
      })
      this.$emit('update:modelValue', names)
    },
    checkMobile() {
      this.isMobile = isMobileDevice()
    }
  }
}
</script>

<style scoped>
.tree-filter {
  width: 100%;
}

/* 自定义 TreeSelect 标签样式，匹配 TagFilter */
.tree-filter :deep(.ant-select-selector) {
  min-height: 28px;
  padding: 2px 4px;
}

.tree-filter :deep(.ant-select-selection-item) {
  height: 28px;
  line-height: 28px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 14px;
  border: 1px solid #d9d9d9;
  background: #1890ff;
  color: #fff;
  margin: 2px 4px 2px 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.tree-filter :deep(.ant-select-selection-item-remove) {
  color: #fff;
  margin-left: 4px;
}

.tree-filter :deep(.ant-select-selection-item-remove:hover) {
  color: rgba(255, 255, 255, 0.8);
}

/* 移动端树形选择器样式 */
.mobile-tree {
  width: 100%;
  padding: 8px 0;
}

.mobile-tree :deep(.ant-tree) {
  background: transparent;
  font-size: 15px;
}

.mobile-tree :deep(.ant-tree-list) {
  padding: 0;
}

.mobile-tree :deep(.ant-tree-list-holder-inner) {
  padding: 0;
}

.mobile-tree :deep(.ant-tree-treenode) {
  padding: 0;
  margin: 0;
}

.mobile-tree :deep(.ant-tree-node-content-wrapper) {
  min-height: 52px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  border-radius: 8px;
  transition: all 0.2s;
  margin: 4px 0;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  user-select: none;
  -webkit-user-select: none;
}

.mobile-tree :deep(.ant-tree-node-content-wrapper) {
  pointer-events: auto;
}

.mobile-tree :deep(.ant-tree-node-content-wrapper:hover) {
  background: #f5f5f5;
}

.mobile-tree :deep(.ant-tree-node-content-wrapper:active) {
  background: #ebebeb;
}

.mobile-tree :deep(.ant-tree-node-content-wrapper.ant-tree-node-selected) {
  background: transparent;
}

.mobile-tree :deep(.ant-tree-title) {
  flex: 1;
  color: rgba(0, 0, 0, 0.85);
  font-size: 15px;
  line-height: 1.5;
  word-break: break-word;
  padding-left: 12px;
  padding-right: 8px;
  min-height: 24px;
  display: flex;
  align-items: center;
  pointer-events: auto;
  cursor: pointer;
}

.mobile-tree :deep(.ant-tree-checkbox) {
  margin-right: 0;
  margin-left: 0;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  padding: 0;
}

.mobile-tree :deep(.ant-tree-checkbox-wrapper) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: -6px 0;
  padding: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  pointer-events: auto;
  z-index: 1;
}

.mobile-tree :deep(.ant-tree-checkbox-inner) {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 2px solid #d9d9d9;
  background-color: #fff;
}

.mobile-tree :deep(.ant-tree-checkbox-checked .ant-tree-checkbox-inner) {
  background-color: #1989fa;
  border-color: #1989fa;
}

.mobile-tree :deep(.ant-tree-checkbox-checked .ant-tree-checkbox-inner::after) {
  width: 6px;
  height: 10px;
  border: 2px solid #fff;
  border-top: 0;
  border-left: 0;
  left: 7px;
  top: 3px;
}

.mobile-tree :deep(.ant-tree-checkbox-indeterminate .ant-tree-checkbox-inner) {
  background-color: #1989fa;
  border-color: #1989fa;
}

.mobile-tree :deep(.ant-tree-checkbox-indeterminate .ant-tree-checkbox-inner::after) {
  width: 8px;
  height: 2px;
  background-color: #fff;
  border: 0;
  left: 7px;
  top: 10px;
}

.mobile-tree :deep(.ant-tree-switcher) {
  width: 44px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  flex-shrink: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
}

.mobile-tree :deep(.ant-tree-switcher-icon) {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.45);
  transition: transform 0.3s;
}

.mobile-tree :deep(.ant-tree-switcher-open .ant-tree-switcher-icon) {
  transform: rotate(90deg);
}

.mobile-tree :deep(.ant-tree-child-tree) {
  margin-left: 0;
  padding-left: 48px;
}

.mobile-tree :deep(.ant-tree-indent) {
  width: 0;
  display: none;
}

.mobile-tree :deep(.ant-tree-indent-unit) {
  width: 0;
}
</style>

<style>
/* 只设置最外层容器的最大高度，让 Ant Design 自己处理滚动 */
.ant-select-dropdown.ant-tree-select-dropdown {
  max-height: 600px !important;
  min-width: 300px !important;
  max-width: 500px !important;
}

/* 确保树节点文本可以完整显示，允许换行 */
.ant-select-dropdown.ant-tree-select-dropdown .ant-select-tree-title {
  white-space: normal !important;
  word-break: break-word !important;
  line-height: 1.5 !important;
}

/* 移动端 TreeSelect 下拉框样式（PC端使用） */
@media (max-width: 767px) {
  .ant-select-dropdown.ant-tree-select-dropdown {
    max-height: 50vh !important;
    min-width: 90vw !important;
    max-width: 90vw !important;
  }

  .ant-select-dropdown.ant-tree-select-dropdown .ant-select-tree-title {
    font-size: 15px !important;
    padding: 8px 0 !important;
  }

  .ant-select-dropdown.ant-tree-select-dropdown .ant-select-tree-node-content-wrapper {
    min-height: 44px !important;
    display: flex !important;
    align-items: center !important;
  }
}
</style>

